/**
 * StoreProducts (route: `/store/:storeId`) — products of a single store.
 *
 * Rewritten on top of:
 *   - design-system primitives (Button, Badge, Card, Skeleton, EmptyState)
 *   - the new <ProductCard /> with the shared `useCartActions` hook
 *
 * Sections:
 *   • Branded store header w/ rating + open status + service radius
 *   • Sticky search (autocomplete) + filter chips (sort / veg / discount)
 *   • Category chip strip
 *   • Either a flat grid (filtered) or category-grouped grids
 *   • Floating cart bar (in-store) — links to /cart?store_id=
 */
import React, { useState, useEffect, useRef } from 'react';
import { Container, Alert } from 'react-bootstrap';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Search, ChevronRight, ArrowLeft, SlidersHorizontal, Star } from 'lucide-react';
import { productsAPI, Product } from '../api/products';
import { storesAPI, Store } from '../api/stores';
import { categoriesAPI, Category } from '../api/categories';
import { Badge, Button, Card, EmptyState, RatingStars, Skeleton, css } from '../design';
import useCartActions from '../hooks/useCartActions';
import ProductCard from '../components/products/ProductCard';
import '../styles/blinkit.css';

const StoreProducts: React.FC = () => {
  const { storeId } = useParams<{ storeId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [store, setStore] = useState<Store | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(searchParams.get('category') || null);
  const [sortBy, setSortBy] = useState<'relevance' | 'price_asc' | 'price_desc' | 'newest' | 'discount'>('relevance');
  const [vegOnly, setVegOnly] = useState(false);
  const [discountOnly, setDiscountOnly] = useState(false);

  const focusId = searchParams.get('focus') ? parseInt(searchParams.get('focus')!) : null;
  const [highlightId, setHighlightId] = useState<number | null>(focusId);

  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchDebounceRef = useRef<NodeJS.Timeout | null>(null);

  const cart = useCartActions();

  useEffect(() => {
    if (storeId) loadData(parseInt(storeId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId]);

  useEffect(() => {
    if (!focusId || products.length === 0) return;
    const t = setTimeout(() => {
      const el = document.getElementById(`product-card-${focusId}`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setTimeout(() => setHighlightId(null), 2500);
    }, 150);
    return () => clearTimeout(t);
  }, [focusId, products.length]);

  const loadData = async (id: number) => {
    setLoading(true);
    setError(null);
    let storeData: Store;
    try {
      storeData = await storesAPI.getStore(id);
    } catch (err: any) {
      const detail = err?.response?.data?.detail || err?.message || 'Failed to load store';
      setError(typeof detail === 'string' ? detail : JSON.stringify(detail));
      setLoading(false);
      return;
    }
    setStore(storeData);

    const [productsResult, catsResult] = await Promise.all([
      productsAPI.listProducts(id, 0, 200, true).catch((e) => {
        console.warn('[StoreProducts] products failed:', e);
        return [] as Product[];
      }),
      categoriesAPI.listCategories().catch(() => [] as Category[]),
    ]);
    setProducts(productsResult);
    setCategories(catsResult);
    cart.primeFromStore(id);
    setLoading(false);
  };

  // Autocomplete
  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    if (!searchQuery || searchQuery.length < 2 || !storeId) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    searchDebounceRef.current = setTimeout(async () => {
      try {
        const results = await productsAPI.autocomplete(searchQuery, parseInt(storeId));
        setSuggestions(results);
        setShowSuggestions(results.length > 0);
      } catch { setSuggestions([]); }
    }, 300);
    return () => { if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current); };
  }, [searchQuery, storeId]);

  const selectedCategoryMeta = selectedCategory
    ? categories.find((c) => c.slug === selectedCategory)
    : null;

  const filteredProducts = products
    .filter((p) => {
      const matchesSearch = !searchQuery || p.name.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory =
        !selectedCategory ||
        (selectedCategoryMeta &&
          (p.category_id === selectedCategoryMeta.id ||
            p.category?.toLowerCase() === selectedCategoryMeta.name.toLowerCase())) ||
        p.category?.toLowerCase() === selectedCategory.toLowerCase();
      const matchesVeg = !vegOnly || p.is_veg;
      const mrp = p.mrp ?? p.price;
      const eff = p.effective_discount_percent ?? (mrp > p.price ? ((mrp - p.price) / mrp) * 100 : 0);
      const matchesDiscount = !discountOnly || eff >= 10;
      return matchesSearch && matchesCategory && matchesVeg && matchesDiscount;
    })
    .sort((a, b) => {
      if (sortBy === 'price_asc') return a.price - b.price;
      if (sortBy === 'price_desc') return b.price - a.price;
      if (sortBy === 'newest') return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      if (sortBy === 'discount') {
        const da = a.effective_discount_percent ?? 0;
        const db = b.effective_discount_percent ?? 0;
        return db - da;
      }
      return 0;
    });

  const productsByCategory = filteredProducts.reduce<Record<string, Product[]>>((acc, p) => {
    const cat = p.category || 'Other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(p);
    return acc;
  }, {});

  const totalCartItems = Array.from(cart.quantities.entries())
    .filter(([k]) => k.startsWith(`${storeId}:`))
    .reduce((sum, [, qty]) => sum + qty, 0);
  const totalCartAmount = Array.from(cart.quantities.entries())
    .filter(([k]) => k.startsWith(`${storeId}:`))
    .reduce((sum, [k, qty]) => {
      const productId = Number(k.split(':')[1]);
      const p = products.find((prod) => prod.id === productId);
      return sum + (p ? (p.effective_price ?? p.price) * qty : 0);
    }, 0);

  if (loading) {
    return (
      <Container style={{ maxWidth: 1100, paddingTop: 16 }}>
        <Skeleton height={120} />
        <div style={{ marginTop: 16, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: 12 }}>
          {Array.from({ length: 12 }).map((_, i) => <Skeleton.ProductCard key={i} />)}
        </div>
      </Container>
    );
  }

  if (error || !store) {
    return (
      <Container className="py-5">
        <Alert variant="danger">{error || 'Store not found'}</Alert>
      </Container>
    );
  }

  return (
    <div className="blinkit-page">
      {/* Store Header */}
      <div
        style={{
          background: `linear-gradient(135deg, ${css.color.primary}, ${css.color.primaryStrong})`,
          color: '#FFFFFF',
          padding: '18px 16px 22px',
          borderRadius: '0 0 22px 22px',
          marginBottom: 12,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
          <button
            type="button"
            onClick={() => navigate(-1)}
            style={{ background: 'rgba(255,255,255,0.18)', border: 'none', borderRadius: 999, padding: 6, color: '#FFFFFF', cursor: 'pointer', display: 'inline-flex' }}
            aria-label="Back"
          >
            <ArrowLeft size={18} />
          </button>
          <h1 style={{ margin: 0, color: '#FFFFFF', fontFamily: css.font.display, fontSize: 22, fontWeight: 800, letterSpacing: '-0.02em' }}>
            {store.business_name}
          </h1>
        </div>
        <div style={{ display: 'flex', gap: 12, fontSize: 13, color: 'rgba(255,255,255,0.92)', flexWrap: 'wrap', alignItems: 'center' }}>
          {(store as any).rating != null && (store as any).rating > 0 && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'rgba(255,255,255,0.16)', padding: '3px 8px', borderRadius: 999 }}>
              <Star size={13} fill="#FFFFFF" strokeWidth={0} />
              {(store as any).rating.toFixed(1)}
            </span>
          )}
          {store.is_open ? <Badge variant="success" size="sm">Open now</Badge> : <Badge variant="danger" size="sm">Closed</Badge>}
          {store.opening_hours && <span>{store.opening_hours} – {store.closing_hours}</span>}
          {store.service_radius_km && <span>Delivers within {store.service_radius_km} km</span>}
        </div>
      </div>

      {/* Sticky search & filters */}
      <div
        style={{
          position: 'sticky',
          top: 'var(--pm-navbar-height, 88px)',
          zIndex: 5,
          background: css.color.bg,
          padding: '8px 16px',
          marginBottom: 12,
          borderBottom: `1px solid ${css.color.border}`,
        }}
      >
        <div style={{ position: 'relative' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              background: css.color.surface,
              border: `1px solid ${css.color.border}`,
              borderRadius: 12,
              padding: '8px 12px',
              boxShadow: css.shadow.sm,
            }}
          >
            <Search size={16} color={css.color.textMuted} />
            <input
              placeholder="Search in this store…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
              style={{
                flex: 1,
                border: 'none',
                outline: 'none',
                background: 'transparent',
                fontSize: 14,
                color: css.color.text,
              }}
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => { setSearchQuery(''); setSuggestions([]); }}
                aria-label="Clear"
                style={{ background: 'none', border: 'none', color: css.color.textMuted, cursor: 'pointer' }}
              >
                ✕
              </button>
            )}
          </div>
          {showSuggestions && (
            <div
              style={{
                position: 'absolute', top: '100%', left: 0, right: 0,
                background: css.color.surface,
                border: `1px solid ${css.color.border}`,
                borderRadius: 10,
                boxShadow: css.shadow.md,
                maxHeight: 200,
                overflowY: 'auto',
                zIndex: 200,
                marginTop: 4,
              }}
            >
              {suggestions.map((s) => (
                <div
                  key={s}
                  style={{ padding: '10px 14px', cursor: 'pointer', fontSize: 14, color: css.color.text }}
                  onMouseDown={() => { setSearchQuery(s); setShowSuggestions(false); }}
                >
                  <Search size={14} style={{ marginRight: 8, color: css.color.textMuted }} />{s}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Filter row */}
        <div style={{ display: 'flex', gap: 8, padding: '8px 0 0', flexWrap: 'wrap', alignItems: 'center' }}>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as any)}
            style={{
              padding: '6px 12px',
              borderRadius: 999,
              border: `1px solid ${css.color.border}`,
              background: css.color.surface,
              color: css.color.text,
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            <option value="relevance">Sort: Relevance</option>
            <option value="price_asc">Price: Low → High</option>
            <option value="price_desc">Price: High → Low</option>
            <option value="discount">Biggest discount</option>
            <option value="newest">Newest</option>
          </select>
          <FilterChip active={vegOnly} onClick={() => setVegOnly((v) => !v)} label="🟢 Veg only" />
          <FilterChip active={discountOnly} onClick={() => setDiscountOnly((v) => !v)} label="🔥 Deals" />
        </div>
      </div>

      {/* Category Chips */}
      <div style={{ padding: '0 16px' }}>
        <div className="category-chips">
          <button
            className={`category-chip ${!selectedCategory ? 'active' : ''}`}
            onClick={() => setSelectedCategory(null)}
          >
            <span className="chip-icon">🏪</span>
            <span className="chip-label">All</span>
          </button>
          {categories.map((cat) => (
            <button
              key={cat.id}
              className={`category-chip ${selectedCategory === cat.slug ? 'active' : ''}`}
              onClick={() => setSelectedCategory(selectedCategory === cat.slug ? null : cat.slug)}
            >
              <span className="chip-icon">{cat.icon}</span>
              <span className="chip-label">{cat.name}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Products */}
      <Container style={{ maxWidth: 1100, padding: '12px 16px' }}>
        {filteredProducts.length === 0 ? (
          <Card padding="lg">
            <EmptyState
              icon="🔎"
              title="Nothing here"
              description="Try a different search term, category, or clear your filters."
              action={{ label: 'Clear filters', onClick: () => { setSearchQuery(''); setSelectedCategory(null); setVegOnly(false); setDiscountOnly(false); } }}
            />
          </Card>
        ) : selectedCategory || searchQuery ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12 }}>
            {filteredProducts.map((p) => (
              <ProductCard
                key={p.id}
                product={p}
                qty={cart.getQty(p)}
                adding={cart.isBusy(p)}
                highlight={highlightId === p.id}
                onAdd={() => cart.add(p)}
                onIncrement={() => cart.increment(p)}
                onDecrement={() => cart.decrement(p)}
              />
            ))}
          </div>
        ) : (
          Object.entries(productsByCategory).map(([cat, prods]) => (
            <div key={cat} style={{ marginBottom: 24 }}>
              <h3 style={{ fontFamily: css.font.display, fontSize: 17, fontWeight: 800, color: css.color.text, margin: '14px 0 10px' }}>
                {cat}
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12 }}>
                {prods.map((p) => (
                  <ProductCard
                    key={p.id}
                    product={p}
                    qty={cart.getQty(p)}
                    adding={cart.isBusy(p)}
                    highlight={highlightId === p.id}
                    onAdd={() => cart.add(p)}
                    onIncrement={() => cart.increment(p)}
                    onDecrement={() => cart.decrement(p)}
                  />
                ))}
              </div>
            </div>
          ))
        )}
      </Container>

      {/* Floating cart bar */}
      {totalCartItems > 0 && (
        <button
          type="button"
          onClick={() => navigate(`/cart?store_id=${storeId}`)}
          style={{
            position: 'fixed',
            left: 16,
            right: 16,
            bottom: 'calc(env(safe-area-inset-bottom, 0px) + var(--pm-bottom-nav-height, 64px) + 8px)',
            zIndex: 1015,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '12px 16px',
            borderRadius: 14,
            background: `linear-gradient(135deg, ${css.color.primary}, ${css.color.primaryStrong})`,
            color: '#FFFFFF',
            border: 'none',
            cursor: 'pointer',
            boxShadow: '0 12px 30px rgba(22,163,74,0.45)',
            maxWidth: 1068,
            marginInline: 'auto',
          }}
        >
          <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            <strong style={{ fontSize: 15 }}>
              {totalCartItems} item{totalCartItems > 1 ? 's' : ''}
            </strong>
            <span style={{ fontSize: 12, opacity: 0.9 }}>₹{totalCartAmount.toFixed(0)}</span>
          </span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontWeight: 700 }}>
            View cart <ChevronRight size={18} />
          </span>
        </button>
      )}

      {/* Avoid unused-symbol warnings for icons referenced inline above */}
      <span style={{ display: 'none' }} aria-hidden>
        <RatingStars rating={0} />
        <SlidersHorizontal size={0} />
      </span>
    </div>
  );
};

const FilterChip: React.FC<{ active: boolean; onClick: () => void; label: string }> = ({ active, onClick, label }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      padding: '6px 12px',
      borderRadius: 999,
      border: `1.5px solid ${active ? css.color.primary : css.color.border}`,
      background: active ? css.color.primarySoft : css.color.surface,
      color: active ? css.color.primaryStrong : css.color.text2,
      fontSize: 12,
      fontWeight: 700,
      cursor: 'pointer',
      transition: 'background 160ms ease, border-color 160ms ease',
    }}
  >
    {label}
  </button>
);

export default StoreProducts;

